import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import analytics, { EVENTS } from 'shared-assets/lib/analytics';
import colors, { defaults } from 'shared-assets/lib/colors';
import rollbar from 'shared-assets/src/errors/errors';

export const Styled = styled.button`
  & {
    background-color: ${colors.button.default.backgroundColor};
    background-image: ${colors.button.default.backgroundImage};
    font-family: ${defaults.fontFamily};
    position: relative;
    display: inline-block;
    padding: 6px 10px;
    border-radius: ${colors.button.borderRadius};
    border: ${colors.button.default.border};
    box-shadow: ${colors.button.default.boxShadow};
    font-size: 1em;
    cursor: pointer;
    -webkit-font-smoothing: antialiased;
    text-decoration: none;
    outline: 0;
    line-height: 1.5;
    font-weight: 400;
    color: ${colors.button.default.color};
    svg path {
      fill: ${colors.button.default.svgColor};
    }

    &.small {
      padding: 3px 8px;
      font-size: 0.85rem;
      font-weight: 400;
    }

    &:disabled {
      box-shadow: 0 0 0 transparent;
      background: ${colors.button.default.disabled.backgroundColor};
      cursor: not-allowed;
      color: ${colors.button.default.disabled.color};
      text-shadow: 0 0 0 transparent;
      border: ${colors.button.default.disabled.border};
      && svg path {
        fill: ${colors.button.default.disabled.svgColor};
      }
    }

    &:hover:not(:disabled),
    &.active:not(:disabled) {
      background-color: ${colors.button.default.hover.backgroundColor};
      background-image: ${colors.button.default.hover.backgroundImage};
    }

    &&:active:not(:disabled),
    &&.active {
      box-shadow: ${colors.button.default.active.boxShadow};
      background: ${colors.button.default.active.backgroundColor};
    }

    &.primary {
      background-color: ${colors.button.primary.backgroundColor};
      background-image: ${colors.button.primary.backgroundImage};
      color: ${colors.button.primary.color};
      border: ${colors.button.primary.border};
      box-shadow: ${colors.button.primary.boxShadow};
      text-shadow: ${colors.button.primary.textShadow};
      font-weight: ${colors.button.fontWeight};
      && svg path {
        fill: ${colors.button.primary.svgColor};
      }

      &:hover:not(:disabled),
      &.active:not(:disabled) {
        background-color: ${colors.button.primary.hover.backgroundColor};
        background-image: ${colors.button.primary.hover.backgroundImage};
      }

      &:disabled {
        border: ${colors.button.primary.disabled.border};
        box-shadow: 0 0 0 transparent;
        color: ${colors.button.primary.disabled.color};
        background-color: ${colors.button.primary.disabled.backgroundColor};
        && svg path {
          fill: ${colors.button.primary.disabled.svgColor};
        }
      }
    }

    &.success {
      background-color: ${colors.fill.success};
      background-image: linear-gradient(#a9c537, #a1bb34);
      border: 1px solid #90a72f;
      box-shadow: inset 0 1px 0 #b2cb4b;
      text-shadow: 0 -1px 0 #98b132;
      color: #fff;
      font-weight: 600;

      &:hover:not(:disabled),
      &.active:not(:disabled) {
        background-color: #a1bb34;
        background-image: linear-gradient(#a1bb34, #90a72f);
      }
    }

    &.error {
      background-color: ${colors.fill.error};
      background-image: ${colors.button.error.backgroundImage};
      border: ${colors.button.error.border};
      box-shadow: ${colors.button.error.boxShadow};
      text-shadow: ${colors.button.error.textShadow};
      color: #fff;
      font-weight: ${colors.button.fontWeight};

      &:hover:not(:disabled),
      &.active:not(:disabled) {
        background-color: ${colors.button.error.hover.backgroundColor};
        background-image: ${colors.button.error.hover.backgroundImage};
      }

      &:disabled {
        background-color: ${colors.button.error.disabled.backgroundColor};
        border: ${colors.button.error.disabled.border};
        box-shadow: 0 0 0 transparent;
        color: ${colors.button.error.disabled.color};
        && svg path {
          fill: ${colors.button.error.disabled.svgColor};
        }
      }
    }

    &.warning {
      background-color: ${colors.fill.warning};
      background-image: linear-gradient(#f0ad4e, #e4a44a);
      border: 1px solid #cc9342;
      box-shadow: inset 0 1px 0 #f2b560;
      text-shadow: 0 -1px 0 #d89c46;
      color: #fff;
      font-weight: 600;

      &:hover:not(:disabled),
      &.active:not(:disabled) {
        background-color: #e4a44a;
        background-image: linear-gradient(#e4a44a, #cc9342);
      }
    }

    &.link {
      background: none;
      border: none;
      box-shadow: none;
      color: ${colors.primary};

      &&:active:not(:disabled),
      &&.active {
        box-shadow: none;
        background: ${colors.button.link.activeColor};
      }

      &:hover:not(:disabled),
      &.active:not(:disabled) {
        background: ${colors.button.link.hoverColor};
      }

      &:disabled {
        cursor: not-allowed;
        color: ${colors.button.link.disabledColor};
      }

      &&.noBtn {
        background: none;
        padding: 0px;
      }
      &&.secondary {
        color: ${colors.secondary};
      }
    }
  }
`;

const Button = React.forwardRef(
  ({ children, track, onClick, ...props }, ref) => {
    let onClickWithTracking = event => {
      let trackingMethod = !!props.trackingProvider
        ? props.trackingProvider()
        : () => {};
      if (!!track && typeof trackingMethod === 'function') {
        // if tracking then prevent default behavior
        event.preventDefault();

        let { name, data } = track;
        name = !name ? EVENTS.BUTTON_CLICKED : name;
        trackingMethod(name, { ...data })
          .then(() => {
            // dont call onClick until tracking is complete
            if (!!onClick) {
              onClick(event);
            }
          })
          .catch((responseCode, responseBody) => {
            rollbar.error(responseCode, responseBody);
            if (!!onClick) {
              onClick(event);
            }
          });
      } else if (!!onClick) {
        onClick(event);
      }
    };

    let onClickCallback = !!track ? onClickWithTracking : onClick;
    return (
      <Styled onClick={onClickCallback} ref={ref} {...props}>
        {children}
      </Styled>
    );
  },
);

Button.displayName = 'Button';

Button.defaultProps = {
  onClick: undefined,
  track: null,
  trackingProvider: () => analytics.track,
};

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  onClick: PropTypes.func,
  track: PropTypes.shape({
    name: PropTypes.oneOf([...Object.values(EVENTS)]),
    data: PropTypes.object,
  }),
  trackingProvider: PropTypes.func, // gives ability to pass in a tracking funciton instead of anayltics.track
  type: PropTypes.oneOf(['submit', 'button', 'reset']).isRequired,
};

export default Button;
